/**
 * Attributes a Premium conversion back to whatever sent the buyer to the page.
 * Upsells link to `/premium?src=<upsell-id>`; this captures that on arrival and
 * replays it onto every downstream analytics event and the checkout token, so
 * `purchase_conversion_events` can be grouped by the campaign that earned the sale.
 */

export const PREMIUM_ENTRY_SOURCE_PARAM = 'src';

const STORAGE_KEY = 'rio-premium-entry-source';
// Long enough to survive reading the page and a login/checkout round-trip, short
// enough that an unrelated later visit is not credited to an earlier campaign.
const MAX_AGE_MS = 30 * 60 * 1000;
export const MAX_ENTRY_SOURCE_LENGTH = 64;

/** Campaign ids only — anything else is dropped rather than stored as a free-text label. */
export function normalizeEntrySource(value: unknown): string | null {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();

  if (!normalized || normalized.length > MAX_ENTRY_SOURCE_LENGTH) {
    return null;
  }

  return /^[a-z0-9][a-z0-9_-]*$/.test(normalized) ? normalized : null;
}

function readEntrySourceFromSearch(search: string): string | null {
  try {
    return normalizeEntrySource(new URLSearchParams(search).get(PREMIUM_ENTRY_SOURCE_PARAM));
  } catch {
    return null;
  }
}

/**
 * Call on Premium page mount. A visit without `src` keeps any source already
 * captured this session, so an in-page navigation does not erase attribution.
 */
export function capturePremiumEntrySource(search?: string): string | null {
  const rawSearch = search ?? (typeof window !== 'undefined' ? window.location.search : '');
  const entrySource = readEntrySourceFromSearch(rawSearch);

  if (!entrySource) {
    return getPremiumEntrySource();
  }

  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ entrySource, savedAt: Date.now() }));
  } catch {
    // Storage unavailable (private mode, quota) — attribution is best-effort only.
  }

  return entrySource;
}

export function getPremiumEntrySource(): string | null {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return null;
    }

    const { entrySource, savedAt } = JSON.parse(stored) || {};
    if (typeof savedAt !== 'number' || Date.now() - savedAt > MAX_AGE_MS) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return normalizeEntrySource(entrySource);
  } catch {
    return null;
  }
}

export function clearPremiumEntrySource(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable — nothing to clear.
  }
}
